(() => {
  'use strict';
  if (customElements.get('phonebar-device-identifier')) return;
  const requests = new Map();
  const codeKey = value => String(value || '').toUpperCase().replace(/[\s-]+/g, '');
  const modelCode = value => {
    const code = codeKey(value);
    if (/^\d{4}$/.test(code)) return 'A'+code;
    return /^(?:SM|GT)[A-Z0-9]+\/(?:DS|DSN)$/.test(code) ? code.replace(/\/(?:DS|DSN)$/, '') : code;
  };
  const validCode = code => /^(?:A\d{4}|(?:SM|GT|SCH|SGH|SPH)[A-Z0-9]+|G[A-Z0-9]{4}|MAC(?:BOOK(?:PRO|AIR)?)?\d+,\d+)$/.test(code);
  const overrideCodes = value => (String(value || '').match(/\s*(?:MacBookPro|MacBookAir|MacBook|Mac)\s*\d+\s*,\s*\d+|[^,;\n]+/gi) || []).map(modelCode);
  const nameKey = value => {
    let text = String(value || '').normalize('NFKD').toLowerCase()
    .replace(/[\u0300-\u036f]/g, '').replace(/[‑–—]/g, '-').replace(/\+/g, ' plus ')
    .replace(/^\s*(?:cases?|covers?)\s+for\s+/, '').replace(/^\s*(?:apple|samsung|google)\s+/, '').replace(/^galaxy\s+/, '')
    .replace(/\s+(?:repairs?|cases?|covers?)(?:\s*&\s*covers)?\s*$/, '')
    .replace(/(\d+(?:\.\d+)?)\s*["″”]/g, '$1 inch ')
    .replace(/(\d)(?:st|nd|rd|th)\b/g, '$1');
    // The numbered iPad has one screen size per generation. Keep sizes for Pro/Air.
    if (/^ipad\s*(?:\(|\d)/.test(text) && /\d\s*(?:generation|gen)\b/.test(text)) text = text.replace(/\b\d+(?:\.\d+)?[\s-]*(?:inch|inches|in)\b/g, ' ');
    return text.replace(/\b(?:generation|gen)\b/g, '')
    .replace(/\b(?:inches|inch|in)\b/g, 'inch').replace(/(\d)\.(\d)/g, '$1d$2')
    .replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
  };

  function safeURL(value, image = false) {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (!['https:', 'http:'].includes(url.protocol)) return null;
      if (!image && url.origin !== window.location.origin) return null;
      return url.href;
    } catch (_) { return null; }
  }

  function getDatabase(url) {
    if (!requests.has(url)) {
      const pending = (async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 15000);
        try {
          const response = await fetch(url, {signal:controller.signal, credentials:'omit'});
          if (!response.ok) throw new Error('Lookup unavailable');
          const data = await response.json();
          if (!Array.isArray(data.devices) || !data.devices.length) throw new Error('Invalid lookup');
          const index = new Map();
          for (const device of data.devices) {
            if (!device.name || !Array.isArray(device.codes)) throw new Error('Invalid device');
            for (const code of device.codes) {
              const key = modelCode(code);
              if (!validCode(key) || index.has(key)) throw new Error('Ambiguous model number');
              index.set(key, device);
            }
          }
          for (const code of data.ambiguousCodes || []) if (!index.has(modelCode(code))) index.set(modelCode(code), null);
          return index;
        } finally { clearTimeout(timer); }
      })();
      requests.set(url, pending);
      pending.catch(() => requests.delete(url));
    }
    return requests.get(url);
  }

  function matchingDestination(items, aliases, cases = false) {
    const broadRanges = new Set(['iphone','ipad','ipad air','ipad mini','ipad pro']);
    const matches = items.filter(item => item && (!cases || (Number(item.count) > 0 && /\b(?:cases?|covers?)\b/i.test(item.title) && !broadRanges.has(nameKey(item.title)))) && aliases.has(nameKey(item.title)) && safeURL(item.url));
    const unique = [...new Map(matches.map(item => [safeURL(item.url), item])).values()];
    return unique.length === 1 ? unique[0] : null;
  }

  function openChat() {
    const launcher = document.getElementById('fixify-live-chat-root')?.shadowRoot?.querySelector('.launch');
    if (!launcher) return false;
    if (launcher.getAttribute('aria-expanded') !== 'true') launcher.click();
    launcher.focus();
    return true;
  }

  class DeviceIdentifier extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.sequence = 0;
      this.input = this.querySelector('[data-number]');
      this.button = this.querySelector('[data-submit]');
      this.status = this.querySelector('[data-status]');
      this.result = this.querySelector('[data-result]');
      this.noMatch = this.querySelector('[data-no-match]');
      try { this.config = JSON.parse(this.querySelector('[data-config]').textContent); }
      catch (_) { this.status.textContent = 'The lookup could not start. Please contact us for help.'; return; }
      this.input.disabled = false;
      this.button.disabled = false;
      const options = {signal:this.controller.signal};
      this.querySelector('[data-form]').addEventListener('submit', event => { event.preventDefault(); this.identify(); }, options);
      this.input.addEventListener('input', () => { this.sequence += 1; this.clearResult(); }, options);
      this.addEventListener('click', event => {
        const link = event.target.closest('a');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const url = new URL(link.href, location.href);
        const fallback = this.querySelector('a[data-chat]');
        const contactURL = fallback ? new URL(fallback.href, location.href) : null;
        const isContact = url.origin === location.origin && (url.pathname.replace(/\/$/, '') === '/pages/contact-us' || (contactURL && url.pathname === contactURL.pathname));
        if (!link.hasAttribute('data-chat') && !isContact) return;
        if (openChat()) event.preventDefault();
      }, options);
    }

    disconnectedCallback() {
      this.sequence += 1;
      this.controller?.abort();
      this.controller = null;
    }

    clearResult() {
      this.result.hidden = true;
      this.noMatch.hidden = true;
      this.status.textContent = '';
      this.input.removeAttribute('aria-invalid');
    }

    showError(message) {
      this.status.textContent = message;
      this.noMatch.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
    }

    async identify() {
      const sequence = ++this.sequence;
      const controller = this.controller;
      this.clearResult();
      const code = modelCode(this.input.value);
      if (!code) { this.showError('Enter your model number, for example A2696.'); this.input.focus(); return; }
      const overrides = (this.config.overrides || []).filter(entry => overrideCodes(entry.codes).includes(code));
      if (overrides.length > 1) { this.showError('This number needs a quick check by our team. Please ask us for help.'); return; }
      const override = overrides[0];
      if (!override && !validCode(code)) {
        if (code.includes('/') || /^[MFNP][A-Z0-9]{5,}$/.test(code)) this.showError('That looks like an Apple part number. Tap Model Number in Settings → General → About to reveal the A-number.');
        else this.showError('Enter the full model number, such as A2696, SM-S928B, GPJ41 or MacBookPro18,1. Please do not enter an IMEI or serial number.');
        return;
      }
      this.button.disabled = true;
      this.status.textContent = 'Identifying your device…';
      try {
        const index = await getDatabase(this.dataset.modelsUrl);
        if (controller.signal.aborted || sequence !== this.sequence) return;
        let device = index.get(code);
        if (index.has(code) && device === null && !override?.name) { this.showError('This identifier is shared by more than one model. Contact us so we can confirm your exact device.'); return; }
        if (!device && override?.name) device = {name:override.name,aliases:[],codes:[code]};
        if (!device) { this.showError('We couldn’t find that model number. Check it and try again, or ask us for help.'); return; }
        this.showDevice(device, code, override);
      } catch (_) {
        if (controller.signal.aborted || sequence !== this.sequence) return;
        if (override?.name) this.showDevice({name:override.name,aliases:[]},code,override);
        else { this.status.textContent = 'The lookup could not load. Please try again or chat with us for help.'; this.noMatch.hidden = false; }
      } finally {
        if (!controller.signal.aborted) this.button.disabled = false;
      }
    }

    showDevice(device, code, override) {
      const aliases = new Set([device.name,...(device.aliases || [])].map(nameKey));
      const repairModels = (this.config.repairs || []).flatMap(group => group.models || []);
      const repair = override?.repair || matchingDestination(repairModels, aliases);
      const cases = override?.cases || matchingDestination(this.config.cases || [], aliases, true);
      this.querySelector('[data-name]').textContent = device.name;
      this.querySelector('[data-detail]').textContent = `Model number: ${code}${device.year ? ' · Introduced '+device.year : ''}`;
      const actions = this.querySelector('[data-actions]');
      actions.replaceChildren();
      for (const [label,destination] of [['View repairs',repair],['Shop cases',cases]]) {
        const href = safeURL(destination?.url);
        if (!href || (label === 'Shop cases' && Number(destination.count) < 1)) continue;
        const link = document.createElement('a'); link.href=href; link.textContent=label; actions.append(link);
      }
      const image = this.querySelector('[data-image]');
      const src = safeURL(repair?.image,true);
      image.hidden = !src;
      image.removeAttribute('src');
      if (src) { image.src=src; image.alt=device.name; image.onerror=()=>{image.hidden=true;}; }
      this.result.hidden = false;
      this.noMatch.hidden = true;
      this.status.textContent = `Identified: ${device.name}.${actions.children.length ? '' : ' Chat with us about repair or case availability for this model.'}`;
    }
  }
  customElements.define('phonebar-device-identifier', DeviceIdentifier);
})();
